"use client";

import {
  useCallback,
  useMemo,
  useState,
  type CSSProperties,
  type FormEvent,
} from "react";
import {
  Bug,
  CheckCircle2,
  Clock3,
  DollarSign,
  HelpCircle,
  Lightbulb,
  Send,
  X,
} from "lucide-react";

import { redactRouteSecrets } from "@/components/system/ErrorCapture";

import { useBreadcrumbs } from "./BreadcrumbProvider";

type IssueCategory = "broken" | "wrong_number" | "idea" | "other";
type IssueSurface = "ops" | "chef" | "cluckup" | "hq" | "supply";
type FeedbackTone = "light" | "dark" | "gradient";
type Tab = "report" | "reports";

type ScreenshotCapture = {
  dataUrl: string;
};

type ModernScreenshotModule = {
  domToJpeg: (
    node: HTMLElement,
    options?: {
      quality?: number;
      backgroundColor?: string;
      scale?: number;
    },
  ) => Promise<string>;
};

type ReportButtonProps = {
  appSurface: IssueSurface;
  tone: FeedbackTone;
  enabled: boolean;
};

type ReporterReport = {
  id: string;
  category: IssueCategory | null;
  appSurface: IssueSurface | null;
  description: string | null;
  resolutionNote: string | null;
  createdAt: string;
  statusLabel: string;
  done: boolean;
};

const CATEGORIES: Array<{
  value: IssueCategory;
  label: string;
  Icon: typeof Bug;
}> = [
  { value: "broken", label: "Something broke", Icon: Bug },
  { value: "wrong_number", label: "Wrong number", Icon: DollarSign },
  { value: "idea", label: "Idea", Icon: Lightbulb },
  { value: "other", label: "Other", Icon: HelpCircle },
];

const TONE_THEME: Record<
  FeedbackTone,
  {
    triggerBg: string;
    triggerFg: string;
    triggerBorder: string;
    sheetBg: string;
    sheetFg: string;
    muted: string;
    border: string;
    panel: string;
    accent: string;
    accentFg: string;
    danger: string;
    screenshotBg: string;
    shadow: string;
  }
> = {
  light: {
    triggerBg: "#fffaf2",
    triggerFg: "#17130c",
    triggerBorder: "rgba(99, 72, 26, 0.28)",
    sheetBg: "#fffaf2",
    sheetFg: "#17130c",
    muted: "#756b5b",
    border: "rgba(89, 72, 40, 0.22)",
    panel: "rgba(255, 255, 255, 0.72)",
    accent: "#c45515",
    accentFg: "#fffaf2",
    danger: "#a13a2a",
    screenshotBg: "#fffaf2",
    shadow: "0 18px 60px rgba(40, 27, 12, 0.22)",
  },
  dark: {
    triggerBg: "#0b100d",
    triggerFg: "#f6f1e7",
    triggerBorder: "rgba(247, 241, 226, 0.16)",
    sheetBg: "#070907",
    sheetFg: "#f6f1e7",
    muted: "#a7ad9f",
    border: "rgba(247, 241, 226, 0.16)",
    panel: "rgba(255, 255, 255, 0.055)",
    accent: "#f0b84c",
    accentFg: "#151009",
    danger: "#ff8777",
    screenshotBg: "#050605",
    shadow: "0 18px 70px rgba(0, 0, 0, 0.45)",
  },
  gradient: {
    triggerBg: "#2e1f89",
    triggerFg: "#ffffff",
    triggerBorder: "rgba(255, 255, 255, 0.24)",
    sheetBg: "#201551",
    sheetFg: "#ffffff",
    muted: "#d7d0ff",
    border: "rgba(255, 255, 255, 0.2)",
    panel: "rgba(255, 255, 255, 0.1)",
    accent: "#7df0cf",
    accentFg: "#11143a",
    danger: "#ffb1c0",
    screenshotBg: "#2e1f89",
    shadow: "0 18px 70px rgba(26, 16, 80, 0.42)",
  },
};

export function ReportButton({ appSurface, tone, enabled }: ReportButtonProps) {
  const breadcrumbs = useBreadcrumbs();
  const theme = TONE_THEME[tone];
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>("report");
  const [category, setCategory] = useState<IssueCategory | null>(null);
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [reports, setReports] = useState<ReporterReport[]>([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState<string | null>(null);

  const styles = useMemo(() => makeStyles(theme), [theme]);

  const loadReports = useCallback(async () => {
    setReportsLoading(true);
    setReportsError(null);
    try {
      const response = await fetch("/api/issues/mine", {
        headers: { accept: "application/json" },
      });
      if (!response.ok) throw new Error(`status ${response.status}`);
      const body = (await response.json()) as { reports?: ReporterReport[] };
      setReports(Array.isArray(body.reports) ? body.reports : []);
    } catch {
      setReportsError("Could not load reports.");
    } finally {
      setReportsLoading(false);
    }
  }, []);

  if (!enabled) return null;

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!category || submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    setSubmitMessage(null);
    try {
      const route = readCurrentRoute();
      const screenshot = await capturePageScreenshot(theme.screenshotBg);
      const response = await fetch("/api/issues", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          kind: "manual",
          severity: "med",
          route,
          description: description.trim() || null,
          category,
          appSurface,
          context: {
            breadcrumbs: breadcrumbs.serializeBreadcrumbs(4096),
            viewport: readViewport(),
            userAgent: readUserAgent(),
            // Diagnostic pair for the iOS PWA bug class: proves whether the
            // app ran standalone and whether the page was pinch/auto-zoomed.
            displayMode: readDisplayMode(),
            viewportScale: readViewportScale(),
          },
          ...(screenshot ? { screenshot } : {}),
        }),
      });
      if (!response.ok) throw new Error(`status ${response.status}`);
      setDescription("");
      setCategory(null);
      setSubmitMessage("Report sent.");
      setTab("reports");
      await loadReports();
    } catch {
      setSubmitError("Could not send report.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <button
        type="button"
        aria-label="Report a problem"
        title="Report a problem"
        data-bc="Report a problem"
        onClick={() => {
          setOpen(true);
          void loadReports();
        }}
        style={styles.trigger}
      >
        <Bug size={15} aria-hidden="true" />
      </button>

      {open ? (
        <div style={styles.overlay}>
          <button
            type="button"
            aria-label="Close report panel"
            data-bc="Close report panel"
            onClick={() => setOpen(false)}
            style={styles.scrim}
          />
          <section
            role="dialog"
            aria-modal="true"
            aria-label="Report a problem"
            style={styles.sheet}
          >
            <header style={styles.header}>
              <div>
                <p style={styles.eyebrow}>Feedback</p>
                <h2 style={styles.title}>Report a problem</h2>
              </div>
              <button
                type="button"
                aria-label="Close report panel"
                data-bc="Close report panel"
                onClick={() => setOpen(false)}
                style={styles.iconButton}
              >
                <X size={16} aria-hidden="true" />
              </button>
            </header>

            <div style={styles.tabs} role="tablist" aria-label="Feedback tabs">
              <button
                type="button"
                role="tab"
                aria-selected={tab === "report"}
                data-bc="Report tab"
                onClick={() => setTab("report")}
                style={tab === "report" ? styles.tabActive : styles.tab}
              >
                Report
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === "reports"}
                data-bc="Your reports tab"
                onClick={() => {
                  setTab("reports");
                  void loadReports();
                }}
                style={tab === "reports" ? styles.tabActive : styles.tab}
              >
                Your reports
              </button>
            </div>

            {tab === "report" ? (
              <form onSubmit={onSubmit} style={styles.form}>
                <div style={styles.categoryGrid}>
                  {CATEGORIES.map(({ value, label, Icon }) => {
                    const active = category === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        aria-pressed={active}
                        data-bc={`Report category ${label}`}
                        onClick={() => setCategory(value)}
                        style={active ? styles.categoryActive : styles.category}
                      >
                        <Icon size={15} aria-hidden="true" />
                        <span>{label}</span>
                      </button>
                    );
                  })}
                </div>

                <label style={styles.textareaLabel}>
                  <span style={styles.labelText}>Details</span>
                  <textarea
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                    maxLength={2_000}
                    rows={4}
                    placeholder="Optional"
                    style={styles.textarea}
                  />
                </label>

                {submitMessage ? <p style={styles.successText}>{submitMessage}</p> : null}
                {submitError ? <p style={styles.errorText}>{submitError}</p> : null}

                <footer style={styles.footer}>
                  <button
                    type="button"
                    data-bc="Cancel report"
                    onClick={() => setOpen(false)}
                    style={styles.secondaryButton}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    data-bc="Send report"
                    disabled={!category || submitting}
                    style={
                      !category || submitting
                        ? styles.primaryButtonDisabled
                        : styles.primaryButton
                    }
                  >
                    <Send size={14} aria-hidden="true" />
                    {submitting ? "Sending" : "Send"}
                  </button>
                </footer>
              </form>
            ) : (
              <ReportsList
                reports={reports}
                loading={reportsLoading}
                error={reportsError}
                onRefresh={loadReports}
                styles={styles}
              />
            )}
          </section>
        </div>
      ) : null}
    </>
  );
}

function ReportsList({
  reports,
  loading,
  error,
  onRefresh,
  styles,
}: {
  reports: ReporterReport[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  styles: ReturnType<typeof makeStyles>;
}) {
  return (
    <div style={styles.reportsPane}>
      <div style={styles.reportsHeader}>
        <span style={styles.labelText}>Your reports</span>
        <button
          type="button"
          data-bc="Refresh reports"
          onClick={onRefresh}
          style={styles.refreshButton}
        >
          Refresh
        </button>
      </div>
      {loading ? <p style={styles.mutedText}>Loading...</p> : null}
      {error ? <p style={styles.errorText}>{error}</p> : null}
      {!loading && !error && reports.length === 0 ? (
        <p style={styles.mutedText}>No reports yet.</p>
      ) : null}
      <div style={styles.reportStack}>
        {reports.map((report) => (
          <article key={report.id} style={styles.reportItem}>
            <div style={styles.reportTopline}>
              <span style={styles.reportTitle}>
                {report.description || labelForCategory(report.category)}
              </span>
              <span
                style={report.done ? styles.statusDone : styles.statusPending}
              >
                {report.done ? (
                  <CheckCircle2 size={13} aria-hidden="true" />
                ) : (
                  <Clock3 size={13} aria-hidden="true" />
                )}
                {report.statusLabel}
              </span>
            </div>
            {report.resolutionNote ? (
              <p style={styles.noteText}>{report.resolutionNote}</p>
            ) : null}
            <p style={styles.metaText}>
              {labelForSurface(report.appSurface)} - {formatDate(report.createdAt)}
            </p>
          </article>
        ))}
      </div>
    </div>
  );
}

async function capturePageScreenshot(
  backgroundColor: string,
): Promise<ScreenshotCapture | null> {
  if (typeof document === "undefined") return null;
  const mod = await importModernScreenshot();
  if (!mod) return null;
  const dataUrl = await mod.domToJpeg(document.documentElement, {
    quality: 0.72,
    backgroundColor,
    scale: 1,
  });
  return { dataUrl };
}

async function importModernScreenshot(): Promise<ModernScreenshotModule | null> {
  try {
    const mod = (await import("modern-screenshot")) as Partial<ModernScreenshotModule>;
    return typeof mod.domToJpeg === "function"
      ? { domToJpeg: mod.domToJpeg }
      : null;
  } catch {
    return null;
  }
}

function readCurrentRoute(): string {
  if (typeof window === "undefined") return "/app";
  // This component POSTs /api/issues itself rather than going through
  // reportClientIssue, so the redaction has to be applied here too — a token
  // in a path segment must not reach the issues table or the ops chat.
  return redactRouteSecrets(
    `${window.location.pathname}${window.location.search}`,
  );
}

function readViewport(): { width: number; height: number } | null {
  if (typeof window === "undefined") return null;
  return { width: window.innerWidth, height: window.innerHeight };
}

function readUserAgent(): string | null {
  if (typeof navigator === "undefined") return null;
  return navigator.userAgent;
}

/** "standalone" when running as an installed PWA, else "browser". */
function readDisplayMode(): string | null {
  if (typeof window === "undefined") return null;
  const standalone =
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return standalone ? "standalone" : "browser";
}

/** Visual-viewport zoom factor — 1 means not zoomed. */
function readViewportScale(): number | null {
  if (typeof window === "undefined") return null;
  return window.visualViewport?.scale ?? null;
}

function labelForCategory(category: IssueCategory | null): string {
  return CATEGORIES.find((item) => item.value === category)?.label ?? "Report";
}

function labelForSurface(surface: IssueSurface | null): string {
  if (surface === "chef") return "Chef";
  if (surface === "cluckup") return "CluckUp";
  if (surface === "hq") return "HQ";
  if (surface === "supply") return "Supply";
  return "Ops";
}

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, {
    timeZone: "Asia/Kuala_Lumpur",
    month: "short",
    day: "numeric",
  });
}

function makeStyles(theme: (typeof TONE_THEME)[FeedbackTone]) {
  const buttonBase: CSSProperties = {
    alignItems: "center",
    borderRadius: 8,
    borderStyle: "solid",
    borderWidth: 1,
    cursor: "pointer",
    display: "inline-flex",
    fontFamily: "inherit",
    fontSize: 13,
    fontWeight: 700,
    gap: 7,
    minHeight: 36,
    transition: "opacity 120ms ease, transform 120ms ease",
  };

  return {
    trigger: {
      // Collapsed edge handle — a slim half-pill docked flush to the right
      // screen edge, lifted above the CluckUp tab-bar zone, so it never
      // covers nav tabs or row actions (operator feedback 2026-06-10). One
      // tap opens the full report sheet.
      alignItems: "center",
      background: theme.triggerBg,
      border: `1px solid ${theme.triggerBorder}`,
      borderRight: 0,
      borderRadius: "12px 0 0 12px",
      bottom: "calc(env(safe-area-inset-bottom, 0px) + 96px)",
      boxShadow: "0 4px 14px rgba(0, 0, 0, 0.22)",
      color: theme.triggerFg,
      cursor: "pointer",
      display: "inline-flex",
      height: 52,
      justifyContent: "center",
      padding: 0,
      position: "fixed",
      right: 0,
      // 27 -> 32: still the slim docked edge handle the operator asked for in
      // 2026-06-10 (it must not cover nav tabs or row actions), but 27px is
      // under any usable thumb-target width. 32 is the narrowest that isn't a
      // mis-tap generator; the 52px height already carries the vertical reach.
      width: 32,
      zIndex: 70,
    } satisfies CSSProperties,
    overlay: {
      bottom: 0,
      left: 0,
      position: "fixed",
      right: 0,
      top: 0,
      zIndex: 80,
    } satisfies CSSProperties,
    scrim: {
      background: "rgba(0, 0, 0, 0.36)",
      border: 0,
      bottom: 0,
      cursor: "default",
      left: 0,
      position: "absolute",
      right: 0,
      top: 0,
    } satisfies CSSProperties,
    sheet: {
      background: theme.sheetBg,
      borderColor: theme.border,
      borderRadius: "8px 8px 0 0",
      borderStyle: "solid",
      borderWidth: "1px 1px 0",
      bottom: 0,
      boxShadow: theme.shadow,
      color: theme.sheetFg,
      display: "flex",
      flexDirection: "column",
      left: "50%",
      maxHeight: "min(76dvh, 620px)",
      maxWidth: 460,
      overflow: "hidden",
      position: "absolute",
      transform: "translateX(-50%)",
      width: "min(calc(100vw - 20px), 460px)",
    } satisfies CSSProperties,
    header: {
      alignItems: "center",
      borderBottom: `1px solid ${theme.border}`,
      display: "flex",
      justifyContent: "space-between",
      padding: "14px 16px",
    } satisfies CSSProperties,
    eyebrow: {
      color: theme.accent,
      fontSize: 10,
      fontWeight: 800,
      letterSpacing: 0,
      margin: 0,
      textTransform: "uppercase",
    } satisfies CSSProperties,
    title: {
      color: theme.sheetFg,
      fontSize: 17,
      lineHeight: 1.25,
      margin: "3px 0 0",
    } satisfies CSSProperties,
    iconButton: {
      ...buttonBase,
      background: theme.panel,
      borderColor: theme.border,
      color: theme.sheetFg,
      height: 34,
      justifyContent: "center",
      minHeight: 34,
      padding: 0,
      width: 34,
    } satisfies CSSProperties,
    tabs: {
      borderBottom: `1px solid ${theme.border}`,
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      padding: 8,
      gap: 6,
    } satisfies CSSProperties,
    tab: {
      ...buttonBase,
      background: "transparent",
      borderColor: "transparent",
      color: theme.muted,
      justifyContent: "center",
      padding: "0 10px",
    } satisfies CSSProperties,
    tabActive: {
      ...buttonBase,
      background: theme.panel,
      borderColor: theme.border,
      color: theme.sheetFg,
      justifyContent: "center",
      padding: "0 10px",
    } satisfies CSSProperties,
    form: {
      display: "flex",
      flex: 1,
      flexDirection: "column",
      gap: 14,
      overflow: "auto",
      padding: 16,
    } satisfies CSSProperties,
    categoryGrid: {
      display: "grid",
      gap: 8,
      gridTemplateColumns: "1fr 1fr",
    } satisfies CSSProperties,
    category: {
      ...buttonBase,
      background: theme.panel,
      borderColor: theme.border,
      color: theme.sheetFg,
      justifyContent: "flex-start",
      minHeight: 42,
      padding: "0 10px",
      width: "100%",
    } satisfies CSSProperties,
    categoryActive: {
      ...buttonBase,
      background: theme.accent,
      borderColor: theme.accent,
      color: theme.accentFg,
      justifyContent: "flex-start",
      minHeight: 42,
      padding: "0 10px",
      width: "100%",
    } satisfies CSSProperties,
    textareaLabel: {
      display: "grid",
      gap: 7,
    } satisfies CSSProperties,
    labelText: {
      color: theme.muted,
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: 0,
      textTransform: "uppercase",
    } satisfies CSSProperties,
    textarea: {
      background: theme.panel,
      border: `1px solid ${theme.border}`,
      borderRadius: 8,
      color: theme.sheetFg,
      font: "inherit",
      // 16px floor — sub-16px text controls make iOS Safari zoom the whole
      // page on focus and stay zoomed after blur (operator bug 0a2cd979
      // captured a 1.23× viewport: exactly 16/13).
      fontSize: 16,
      lineHeight: 1.5,
      minHeight: 98,
      outline: "none",
      padding: 10,
      resize: "vertical",
    } satisfies CSSProperties,
    footer: {
      alignItems: "center",
      display: "flex",
      gap: 8,
      justifyContent: "flex-end",
      marginTop: "auto",
    } satisfies CSSProperties,
    secondaryButton: {
      ...buttonBase,
      background: "transparent",
      borderColor: theme.border,
      color: theme.sheetFg,
      padding: "0 13px",
    } satisfies CSSProperties,
    primaryButton: {
      ...buttonBase,
      background: theme.accent,
      borderColor: theme.accent,
      color: theme.accentFg,
      padding: "0 13px",
    } satisfies CSSProperties,
    primaryButtonDisabled: {
      ...buttonBase,
      background: theme.panel,
      borderColor: theme.border,
      color: theme.muted,
      cursor: "not-allowed",
      opacity: 0.66,
      padding: "0 13px",
    } satisfies CSSProperties,
    successText: {
      color: theme.accent,
      fontSize: 12,
      fontWeight: 700,
      margin: 0,
    } satisfies CSSProperties,
    errorText: {
      color: theme.danger,
      display: "flex",
      fontSize: 12,
      fontWeight: 700,
      gap: 6,
      margin: 0,
    } satisfies CSSProperties,
    mutedText: {
      color: theme.muted,
      fontSize: 13,
      lineHeight: 1.5,
      margin: 0,
    } satisfies CSSProperties,
    reportsPane: {
      display: "flex",
      flex: 1,
      flexDirection: "column",
      gap: 12,
      overflow: "auto",
      padding: 16,
    } satisfies CSSProperties,
    reportsHeader: {
      alignItems: "center",
      display: "flex",
      justifyContent: "space-between",
      gap: 10,
    } satisfies CSSProperties,
    refreshButton: {
      ...buttonBase,
      background: theme.panel,
      borderColor: theme.border,
      color: theme.sheetFg,
      minHeight: 30,
      padding: "0 10px",
    } satisfies CSSProperties,
    reportStack: {
      display: "grid",
      gap: 8,
    } satisfies CSSProperties,
    reportItem: {
      background: theme.panel,
      border: `1px solid ${theme.border}`,
      borderRadius: 8,
      padding: 11,
    } satisfies CSSProperties,
    reportTopline: {
      alignItems: "flex-start",
      display: "flex",
      gap: 10,
      justifyContent: "space-between",
    } satisfies CSSProperties,
    reportTitle: {
      color: theme.sheetFg,
      fontSize: 13,
      fontWeight: 700,
      lineHeight: 1.35,
      minWidth: 0,
    } satisfies CSSProperties,
    statusPending: {
      alignItems: "center",
      background: theme.panel,
      border: `1px solid ${theme.border}`,
      borderRadius: 999,
      color: theme.muted,
      display: "inline-flex",
      flexShrink: 0,
      fontSize: 11,
      fontWeight: 800,
      gap: 5,
      padding: "3px 7px",
    } satisfies CSSProperties,
    statusDone: {
      alignItems: "center",
      background: theme.accent,
      border: `1px solid ${theme.accent}`,
      borderRadius: 999,
      color: theme.accentFg,
      display: "inline-flex",
      flexShrink: 0,
      fontSize: 11,
      fontWeight: 800,
      gap: 5,
      padding: "3px 7px",
    } satisfies CSSProperties,
    noteText: {
      color: theme.sheetFg,
      fontSize: 12,
      lineHeight: 1.45,
      margin: "8px 0 0",
    } satisfies CSSProperties,
    metaText: {
      color: theme.muted,
      fontSize: 11,
      margin: "8px 0 0",
    } satisfies CSSProperties,
  };
}
